"use client"

const GameCard = ({ icon, iconClass, name, description, appName, onOpenGame, color }) => {
  return (
    <div 
      className={`${iconClass} p-6 rounded-2xl glass-effect cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-xl group border border-white/10`}
      onClick={() => onOpenGame(appName)}
    >
      <div className="flex flex-col items-center text-center space-y-3">
        <div className="p-4 rounded-xl bg-black/20 group-hover:bg-black/30 transition-colors">
          <i className={`${icon} ${color} text-4xl`}></i>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors">
            {name}
          </h3>
          <p className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors">
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function GamesApp({ onOpenApp }) {
  const games = [
    { 
      icon: 'fas fa-gamepad', 
      iconClass: 'bg-gradient-to-br from-purple-500/20 to-pink-500/20', 
      name: 'Tetris', 
      description: 'Classic puzzle game',
      appName: 'tetris', 
      color: 'text-purple-400' 
    },
    { 
      icon: 'fas fa-dragon', 
      iconClass: 'bg-gradient-to-br from-green-500/20 to-emerald-500/20', 
      name: 'Snake', 
      description: 'Retro arcade favorite',
      appName: 'snake', 
      color: 'text-green-400' 
    },
    { 
      icon: 'fas fa-bomb', 
      iconClass: 'bg-gradient-to-br from-rose-500/20 to-orange-500/20', 
      name: 'Minesweeper', 
      description: 'Classic puzzle sweeper',
      appName: 'minesweeper', 
      color: 'text-rose-400' 
    },
    { 
      icon: 'fas fa-xmarks-lines', 
      iconClass: 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20', 
      name: 'Tic-Tac-Toe', 
      description: 'Tactical grid vs AI',
      appName: 'tictactoe', 
      color: 'text-blue-400' 
    },
    { 
      icon: 'fas fa-table-tennis-paddle-ball', 
      iconClass: 'bg-gradient-to-br from-amber-500/20 to-orange-500/20', 
      name: 'Arkanoid', 
      description: 'Retro brick breaker & Vaus odyssey',
      appName: 'arkanoid', 
      color: 'text-amber-400' 
    },
    { 
      icon: 'fas fa-chess', 
      iconClass: 'bg-gradient-to-br from-yellow-500/20 to-amber-500/20', 
      name: 'Chess', 
      description: 'Strategic board game vs AI or local',
      appName: 'chess', 
      color: 'text-yellow-400' 
    },
    { 
      icon: 'fas fa-space-shuttle', 
      iconClass: 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20', 
      name: 'Space Invaders', 
      description: 'Retro arcade shooter & alien armada',
      appName: 'spaceinvaders', 
      color: 'text-blue-400' 
    }
  ]

  const handleOpenGame = (appName) => {
    // If onOpenApp is passed from parent, use it (for when launched from desktop)
    if (onOpenApp) {
      onOpenApp(appName)
    } else {
      // For now, we'll just log - this would need to be connected to the main app
      console.log(`Opening ${appName}`)
    }
  }

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-gray-900 via-purple-900/20 to-pink-900/20 text-white overflow-hidden">
      {/* Header */}
      <div className="bg-black/30 p-6 border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-400/30">
            <i className="fas fa-gamepad text-pink-400 text-3xl"></i>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">
              Game Center
            </h1>
            <p className="text-gray-300 text-sm">
              Choose your adventure • {games.length} games available
            </p>
          </div>
        </div>
      </div>

      {/* Games Grid */}
      <div className="flex-1 p-6 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {games.map((game, index) => (
            <GameCard 
              key={index}
              icon={game.icon}
              iconClass={game.iconClass}
              name={game.name}
              description={game.description}
              appName={game.appName}
              onOpenGame={handleOpenGame}
              color={game.color}
            />
          ))}
        </div>

        {/* Coming Soon Section */}
        <div className="mt-12 text-center">
          <h2 className="text-xl font-bold text-gray-400 mb-4">Coming Soon</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="bg-gray-800/30 p-6 rounded-2xl border border-gray-600/30 cursor-not-allowed opacity-60">
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-4 rounded-xl bg-black/20">
                  <i className="fas fa-meteor text-violet-400 text-4xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-400">Asteroids</h3>
                  <p className="text-sm text-gray-500">Vector space shooter</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/30 p-6 rounded-2xl border border-gray-600/30 cursor-not-allowed opacity-60">
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-4 rounded-xl bg-black/20">
                  <i className="fas fa-ghost text-amber-400 text-4xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-400">Pac-Mazes</h3>
                  <p className="text-sm text-gray-500">Retro maze runner</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-12 bg-black/20 rounded-2xl p-6 border border-white/10">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <i className="fas fa-chart-bar text-blue-400"></i>
            Gaming Stats
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <div className="text-2xl font-bold text-green-400">{games.length}</div>
              <div className="text-sm text-gray-300">Games Available</div>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <div className="text-2xl font-bold text-purple-400">2</div>
              <div className="text-sm text-gray-300">Coming Soon</div>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <div className="text-2xl font-bold text-pink-400">∞</div>
              <div className="text-sm text-gray-300">Fun Level</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
